import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useT, useI18nReady } from "./TextProvider";

import s from '../modules/test.module.css';
import {
  getCurrentUser,
  getStudentProgress,
  updateTartib,
  incrementTestAttempts,
  setTestBlocked,
  revertProgressToBlockStart,
  findBlockStartTartib,
  resetTestAttempts,
} from '../utils/progressService';

// Простая функция перевода с fallback значениями
const t = (key) => {
  const translations = {
    'test': 'Nazorat ishi',
    'question': 'Savol',
    'sendAnswer': 'Javobni yuborish',
    'sent': 'Javob yuborildi',
    'alreadySent': 'Siz allaqachon bu savolga javob bergansiz',
    'enterSoon': 'Sizning javobingiz qabul qilindi. Baholash tez orada amalga oshiriladi.',
    'close': 'Yopish',
    'yourRating': 'Sizning bahoingiz',
    'ball': 'ball',
    'comment': 'Izoh',
    'enterImg': 'Rasmni qo\'shish',
    'enterUrlImg': 'Rasm URL manzilingini kiriting',
    'add': 'Qo\'shish',
    'cansel': 'Bekor qilish',
    'enterLink': 'Havolani qo\'shish',
    'enterUrlLink': 'Havolaning URL manzilingini kiriting',
    'removeLink': 'Havolani o\'chirish',
    'clear': 'Tozalash',
    'list': 'Ro\'yxat',
    'topicsList': 'Mavzular ro\'yxati',
  };
  return translations[key] || key;
};

// TODO: remove before production — skips AI calls and auto-passes tests
const BYPASS_AI = false;

export const Test = () => {
  const { kursId, savolId } = useParams();
    const n = useT();
    const ready = useI18nReady();
  const navigate = useNavigate();

  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [testResult, setTestResult] = useState(null); // 'passed', 'failed', 'blocked'
  const [attemptsLeft, setAttemptsLeft] = useState(Number(n("attemptNum")));
  const [canRetakeAnswer, setCanRetakeAnswer] = useState(false);

  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const [imageValue, setImageValue] = useState('');
  const [linkValue, setLinkValue] = useState('');

  // Foydalanuvchi ma'lumotlarini localStorage'dan yoki sessionStorage'dan olamiz
  let currentUserId = null;
  try {
    // Сначала проверяем localStorage
    let u = JSON.parse(localStorage.getItem("auth:user") || "null");
    
    // Если не найдено в localStorage, проверяем sessionStorage
    if (!u) {
      u = JSON.parse(sessionStorage.getItem("auth:user") || "null");
    }
    
    currentUserId = u?.id ?? null;
    console.log("User ID from storage:", currentUserId, "from localStorage:", !!localStorage.getItem("auth:user"), "from sessionStorage:", !!sessionStorage.getItem("auth:user"));
  } catch (e) {
    console.warn("Failed to parse user from storage:", e);
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Strike,
      Code,
      Blockquote,
      Image,
      Link.configure({ openOnClick: true }),
    ],
    content: '',
  });

  useEffect(() => {
    console.log("useEffect triggered - editor:", !!editor, "currentUserId:", currentUserId, "kursId:", kursId);
    
    if (!editor || !currentUserId) {
      console.log("Early return - editor or currentUserId missing");
      return;
    }
  
    const fetchData = async () => {
      try {
        // Сбрасываем состояние перед загрузкой новых данных
        setHasAnswered(false);
        setScore(null);
        setComment('');
        setStatus('');
        setQuestion(null);
        setTestResult(null);
        setLoading(true);
        setCanRetakeAnswer(false);

        // Получаем количество ответов (A)
        const answersRes = await fetch(`https://apitr.subxonqulov.uz/api/yozma-javoblar/?kurs=${kursId}&user=${currentUserId}`);
        
        if (!answersRes.ok) {
          console.error(`Javoblarni olishda xato: ${answersRes.status}`);
          throw new Error(`HTTP ${answersRes.status}`);
        }

        const answersText = await answersRes.text();
        let answers = [];
        try {
          answers = JSON.parse(answersText);
        } catch (e) {
          console.error("JSON javoblarini tahlil qilishda xato:", e, "Matn:", answersText.substring(0, 200));
          answers = [];
        }

        // 🔑 ИСПРАВЛЕНИЕ: Считаем только ответы на ТЕКУЩИЙ вопрос (savolId)
        // Если savolId есть - фильтруем по nему, если нет - берем все ответы курса
        const relevantAnswers = savolId 
          ? answers.filter(a => String(a.savol) === String(savolId))
          : answers;
        
        // A = количество ответов на текущий вопрос/курс
        const A = relevantAnswers.length;
        console.log("=== ANSWER RETAKE CHECK ===");
        console.log("savolId:", savolId);
        console.log("All answers count:", answers.length);
        console.log("Relevant answers count (A):", A);
        console.log("Relevant answers:", relevantAnswers);

        // Получаем test_attempts_count из xolat (B)
        let B = null;
        try {
          const progressRes = await fetch(`https://apitr.subxonqulov.uz/api/xolat/?fan=${kursId}&user=${currentUserId}`);
          if (progressRes.ok) {
            const progressData = await progressRes.json();
            const progress = Array.isArray(progressData) && progressData.length > 0 
              ? progressData[0] 
              : null;
            B = progress?.test_attempts_count ?? null;
            
            console.log("progressData:", progressData);
            console.log("progress (first item):", progress);
          }
        } catch (err) {
          console.warn("Error fetching progress data:", err);
        }

        console.log("B (test_attempts_count из xolat):", B);
        console.log("A === B:", A === B);
        console.log("============================");

        // Если savolId передан (конкретный вопрос), ищем последний ответ для этого вопроса
        // Если нет — ищем последний ответ в курсе
        const relevantAnswers_full = savolId 
          ? answers.filter(a => String(a.savol) === String(savolId))
          : answers;
        
        const relevantAnswer = relevantAnswers_full.length > 0 
          ? relevantAnswers_full[relevantAnswers_full.length - 1] 
          : null;
  
        if (relevantAnswer) {
          // ПРОВЕРКА: A === B?
          if (A === B) {
            // ✅ Студент может повторно отправить ответ
            console.log("✅ Student can retake answer (A === B)");
            setCanRetakeAnswer(true);
            setHasAnswered(false);  // Кнопка активная
            
            // Очищаем редактор для новый ответ
            if (editor && editor.commands) {
              editor.commands.setContent('');
              editor.setOptions({ editable: true });
            }
            
            setStatus('');
          } else {
            // ❌ Показываем прошлый ответ (не можем повторить)
            console.log("❌ Student cannot retake answer (A !== B)");
            setCanRetakeAnswer(false);
            setHasAnswered(true);  // Кнопка неактивная
            setStatus(t('alreadySent'));
    
            if (editor && editor.commands) {
              editor.commands.setContent(relevantAnswer.javob || '');
              editor.setOptions({ editable: false });
            }
            
            // Показываем балл, коммент только если A !== B
            if (relevantAnswer.ball !== null) {
              setScore(relevantAnswer.ball);
              if (relevantAnswer.ball >= Number(n("MinNum"))) {
                setTestResult('passed');
              } else {
                setTestResult('failed');
              }
            }
            if (relevantAnswer.komment) setComment(relevantAnswer.komment);
          }

          // Загружаем полную информацию о вопросе
          try {
            const questionRes = await fetch(`https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/${relevantAnswer.savol}/`);
            if (questionRes.ok) {
              const questionData = await questionRes.json();
              setQuestion({
                id: questionData.id,
                text: questionData.savol_uz || questionData.savol || 'Savol',
                tartib: questionData.tartib,
              });
            } else {
              setQuestion({
                id: relevantAnswer.savol,
                text: relevantAnswer.savol_text || 'Savol',
              });
            }
          } catch (err) {
            console.error('Savol yuklashda xato:', err);
            setQuestion({
              id: relevantAnswer.savol,
              text: relevantAnswer.savol_text || 'Savol',
            });
          }
        } else {
          // Нет ответа — загружаем новый вопрос
          setHasAnswered(false);
          
          if (savolId) {
            // Загружаем конкретный вопрос по ID
            try {
              const questionRes = await fetch(`https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/${savolId}/`);
              if (questionRes.ok) {
                const questionData = await questionRes.json();
                setQuestion(questionData);
              } else {
                setStatus('⚠️ Savol topilmadi');
              }
            } catch (err) {
              console.error('Savol yuklashda xato:', err);
              setStatus('⚠️ Savol yuklashda xato');
            }
          } else {
            // Загружаем случайный вопрос из курса
            const questionsRes = await fetch(`https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/?kurs=${kursId}`);
            
            if (!questionsRes.ok) {
              console.error(`Savollarni olishda xato: ${questionsRes.status}`);
              throw new Error(`HTTP ${questionsRes.status}`);
            }

            const questionsText = await questionsRes.text();
            let questions = [];
            try {
              questions = JSON.parse(questionsText);
            } catch (e) {
              console.error("JSON savollarini tahlil qilishda xato:", e, "Matn:", questionsText.substring(0, 200));
              questions = [];
            }
            
            if (Array.isArray(questions) && questions.length > 0) {
              const randomIndex = Math.floor(Math.random() * questions.length);
              const selectedQuestion = questions[randomIndex];
              setQuestion(selectedQuestion);
            } else {
              setStatus('⚠️ Bu kurs uchun savollar topilmadi');
            }
          }
        }
  
      } catch (error) {
        console.error('Yuklashda xato:', error);
        setStatus('❌ Savol yuklashda xato. Konsolни tekshiring.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [editor, currentUserId, kursId, savolId]);
  

  const handleSubmit = async () => {
    if (!editor || !question || !currentUserId) return;
    const html = editor.getHTML();

    setIsSubmitting(true);

    if (BYPASS_AI) {
      const bypassRes = await fetch('https://apitr.subxonqulov.uz/api/yozma-javoblar/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          javob: html,
          kurs: kursId,
          savol: question.id,
          user: currentUserId,
          ball: 100,
          komment: '',
        }),
      });
      editor.commands.clearContent();
      setHasAnswered(true);
      editor.setOptions({ editable: false });
      setStatus("✅ (AI o'chirilgan - test o'tdi)");
      setShowModal(true);
      if (bypassRes.ok) {
        await handleTestResult(100);
      }
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://apitr.subxonqulov.uz/api/yozma-javoblar/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          javob: html,
          kurs: kursId,
          savol: question.id,
          user: currentUserId,
          ball: null,
          komment: '',
        }),
      });

      if (response.ok) {
        setStatus(t('sent'));
        setShowModal(true);

        editor.commands.clearContent();
        setHasAnswered(true);
        editor.setOptions({ editable: false });

        // Ждем балл от ИИ (polling)
        const createdAnswer = await response.json().catch(() => null);
        if (createdAnswer?.id) {
          await pollForScore(createdAnswer.id);
        }

      } else {
        setStatus('❌ Yuborishda xato.');
      }
    } catch (error) {
      console.error('Ulanishda xato:', error);
      setStatus('❌ Ulanishda xato.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Polling для получения балла от ИИ
  const pollForScore = async (answerId) => {
    const maxAttempts = 30; // 30 * 2000ms = 60 seconds
    let attempt = 0;

    const pollInterval = setInterval(async () => {
      attempt++;
      
      try {
        const response = await fetch(`https://apitr.subxonqulov.uz/api/yozma-javoblar/${answerId}/`);
        if (!response.ok) {
          clearInterval(pollInterval);
          return;
        }

        const data = await response.json();

        // Если есть балл, обработаем его
        if (data.ball !== null && data.ball !== undefined) {
          clearInterval(pollInterval);
          
          setScore(data.ball);
          if (data.komment) {
            setComment(data.komment);
          }

          // Обработаем результат теста
          await handleTestResult(data.ball);
        }

      } catch (err) {
        console.warn("Polling error:", err);
      }

      // Выходим если истек timeout
      if (attempt >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, 2000); // Проверяем каждые 2 секунды
  };

  // Обработка результата теста
  const handleTestResult = async (ball) => {
    try {
      const progressData = await getStudentProgress(kursId, currentUserId);
      if (!progressData || !progressData.id) {
        console.warn("Could not get progress data");
        return;
      }

      const PASSING_SCORE = Number(n("MinNum"));

      console.log("Test result handling:", {
        ball: ball,
        passingScore: PASSING_SCORE,
        testBlocked: progressData.test_blocked,
        testAttemptsCount: progressData.test_attempts_count,
      });

      if (ball >= PASSING_SCORE) {
        // ✅ ТЕСТ ПРОЙДЕН - увеличиваем tartib на 1 и сохраняем mashqtartib
        setTestResult('passed');
        
        const newTartib = Number(progressData.tartib ?? 0) + 1;
        const testTartib = Number(question.tartib ?? progressData.tartib);
        
        console.log("Test PASSED! Updating tartib from", progressData.tartib, "to", newTartib);
        console.log("Saving mashqtartib =", testTartib);
        
        // Если контрольная была заблокирована, разблокируем после успеха
        if (progressData.test_blocked) {
          console.log("Unblocking test after successful attempt");
          await resetTestAttempts(progressData.id);
        }
        
        // Обновляем tartib и mashqtartib
        const updated = await updateTartib(progressData.id, newTartib);
        
        // Сохраняем mashqtartib и обнуляем test_attempts_count на сервер
        if (updated) {
          try {
            await fetch(`https://apitr.subxonqulov.uz/api/xolat/${progressData.id}/`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mashqtartib: testTartib,
                test_attempts_count: 0,
              }),
            });
            console.log("Successfully saved mashqtartib:", testTartib, "and reset test_attempts_count to 0");
          } catch (err) {
            console.warn("Error saving mashqtartib:", err);
          }
          
          console.log("Successfully updated tartib to:", updated.tartib);
          setStatus('✅ Siz nazorat ishini topshirdingiz!');
        }
      } else {
        // ❌ ТЕСТ НЕ ПРОЙДЕН - УВЕЛИЧИВАЕМ test_attempts_count и проверяем блокировку
        setTestResult('failed');
        
        const currentAttempts = Number(progressData.test_attempts_count ?? 0);
        const newAttempts = currentAttempts + 1;
        
        console.log("Test FAILED! Current attempts:", currentAttempts, "New attempts:", newAttempts);
        
        // Откат: tartib = mashqtartib + 1
        const mashqTartib = Number(progressData.mashqtartib ?? 0);
        const revertTartib = mashqTartib + 1;
        
        console.log("Reverting tartib to:", revertTartib, "(mashqtartib:", mashqTartib, "+ 1)");
        
        // Обновляем tartib на сервере
        try {
          const updateRes = await fetch(`https://apitr.subxonqulov.uz/api/xolat/${progressData.id}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tartib: revertTartib,
              test_attempts_count: newAttempts,
            }),
          });
          
          if (updateRes.ok) {
            console.log("Successfully reverted tartib and increased attempts");
          }
        } catch (err) {
          console.warn("Error updating progress:", err);
        }
        
        // Проверяем: если test_attempts_count === 3, только тогда блокируем
        if (newAttempts == Number(n("attemptNum"))) {
          console.log("test_attempts_count reached 3! Blocking test");
          
          try {
            await setTestBlocked(progressData.id, true);
            console.log("Test blocked successfully");
          } catch (err) {
            console.warn("Error setting test blocked:", err);
          }
          
          setStatus(`❌ Maqsad bajarilmadi ${Number(n("attemptNum"))} martaga. Darslarni qayta o'qib chiqing va qayta urinib ko'ring.`);
        } else {
          // Если попыток < 3, показываем сообщение о количестве оставшихся попыток
          const attemptsLeft = Number(n("attemptNum")) - newAttempts;
          console.log("Attempts left:", attemptsLeft);
          setAttemptsLeft(attemptsLeft);
          
          setStatus(`❌ Maqsad bajarilmadi. Qoldiq urinishlar: ${attemptsLeft}/${Number(n("attemptNum"))}`);
        }
      }
    } catch (e) {
      console.warn("Error handling test result:", e);
    }
  };

  const MenuBar = ({ editor }) => {
    if (!editor) return null;

    return (
      <div className={s.menuBar}>
        <button onClick={() => editor.chain().focus().toggleBold().run()} title="Qalin"><strong>B</strong></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} title="Kursiv"><i>I</i></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} title="Tagiga chizish"><u>U</u></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()} title="O'rtasiga chizish"><s>S</s></button>
        <button onClick={() => editor.chain().focus().toggleCode().run()} title="Kod">{'</>'}</button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Iqtibos">❝</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Sarlavha 1">H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Sarlavha 2">H2</button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} title="Markerli ro'yxat">•</button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Raqamli ro'yxat">1.</button>
        <button onClick={() => editor.chain().focus().unsetAllMarks().run()} title="Formatlashni tozalash">✕</button>
        <button onClick={() => setImageModalOpen(true)} title="Rasmni qo'shish">🖼️</button>
        <button onClick={() => setLinkModalOpen(true)} title="Havolani qo'shish">🔗</button>
        <button onClick={() => editor.chain().focus().unsetLink().run()} title="Havolani o'chirish">❌</button>
      </div>
    );
  };

  return (
    <div className={s.container}>
      <h2 className={s.title}>{t('test')}</h2>
      <button onClick={() => navigate(`/mappage/${kursId}`)} className={s.topicsButton}>
        {t('topicsList')}
      </button>

      {loading ? (
        <p className={s.loadingText}>Savol yuklanyapti...</p>
      ) : (
        <>
          {question && (
            <div className={s.question}>
              <strong>
                {t('question')}
                {hasAnswered && testResult === 'passed' && ' ✅'}
                {hasAnswered && testResult === 'failed' && ' ❌'}
                :
              </strong>
              {question.text ? (
                <div dangerouslySetInnerHTML={{ __html: question.text }} />
              ) : question.savol_uz ? (
                <div dangerouslySetInnerHTML={{ __html: question.savol_uz }} />
              ) : (
                <p>Savol matni</p>
              )}
            </div>
          )}

          <MenuBar editor={editor} />

          <div className={s.editorBox}>
            <EditorContent editor={editor} />
          </div>

          <button
            onClick={handleSubmit}
            className={s.submitButton}
            disabled={hasAnswered || isSubmitting || testResult === 'blocked'}
          >
            {testResult === 'blocked' ? '🔒 Bloklandirilgan' : t('sendAnswer')}
          </button>

          {status && <p className={s.statusMessage}>{status}</p>}

          {score !== null && (
            <p className={s.score}>
              <strong>{t('yourRating')}:</strong> {score} {t('ball')}
              {score >= Number(n("MinNum")) ? ' ✅' : ' ❌'}
            </p>
          )}

          {attemptsLeft > 0 && testResult === 'failed' && (
            <p className={s.attemptsInfo}>
              Qolgan urinishlar: <strong>{attemptsLeft}/{Number(n("attemptNum"))}</strong>
            </p>
          )}

          {comment && (
            <div className={s.comment}>
              <strong>{t('comment')}:</strong>
              <p>{comment}</p>
            </div>
          )}
        </>
      )}

      {isImageModalOpen && (
        <div className={s.modalOverlay} onClick={() => setImageModalOpen(false)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('enterImg')}</h3>
            <input
              type="text"
              value={imageValue}
              onChange={(e) => setImageValue(e.target.value)}
              placeholder={t('enterUrlImg')}
              className={s.modalInput}
            />
            <div className={s.modalActions}>
              <button
                onClick={() => {
                  if (imageValue.trim()) {
                    editor.chain().focus().setImage({ src: imageValue }).run();
                    setImageValue('');
                    setImageModalOpen(false);
                  }
                }}
                className={s.modalButton}
              >
                {t('add')}
              </button>
              <button onClick={() => setImageModalOpen(false)} className={s.modalButton}>
                {t('cansel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLinkModalOpen && (
        <div className={s.modalOverlay} onClick={() => setLinkModalOpen(false)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('enterLink')}</h3>
            <input
              type="text"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder={t('enterUrlLink')}
              className={s.modalInput}
            />
            <div className={s.modalActions}>
              <button
                onClick={() => {
                  if (linkValue.trim()) {
                    editor.chain().focus().extendMarkRange('link').setLink({ href: linkValue }).run();
                    setLinkValue('');
                    setLinkModalOpen(false);
                  }
                }}
                className={s.modalButton}
              >
                {t('add')}
              </button>
              <button onClick={() => setLinkModalOpen(false)} className={s.modalButton}>
                {t('cansel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className={s.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('sent')}</h3>
            <p>{t('enterSoon')}</p>
            <div className={s.modalActions}>
              <button className={s.modalButton} onClick={() => setShowModal(false)}>
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};